import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  CustomForm,
  CustomFormItem,
  CustomNumberInput,
  CustomSpinSelect,
  OptionSpin,
  Text,
  CustomCheckbox,
  useText
} from '@src/components';
import { Form, Row, Col, Button, Modal, notification } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import {
  getVehicleLimitTypes,
  getVehicleLimitValueTypes,
  resetVehicleLimitWithValueTypes,
} from '@src/store/slice/addNewCarStore/addCarPlateDetailSlice';
import {
  getCustomerVousList,
  getVehicleDeviceTypeList,
  resetCurrentLoadAddNewCarType,
} from '@src/store/slice/addNewCarStore/addCarTypeSlice';
import {
  postVehicleDeviceTypes,
  getVehicleDetailList,
  incrementActiveTab,
} from '@src/store/slice/vehicleManagementStore/vehicleManagementDetailSlice';
import '@src/styles/addNewCar/addCarSetLimitType.scss';
import '@src/styles/vehicleManagement/addVehicleModal.scss';
import { useLanguageChange } from '@src/hooks/useLanguageChange';
import { useDaysOfWeek } from '@src/hooks/useDaysOfWeek';

const AddVehicleModal = ({
  isVisible,
  handleOk,
  handleCancel,
  vehicleId,
  vehicleListDetailDeviceTypes,
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [dontShowUTTSChoose, setDontShowUTTSChoose] = useState(false);
  const [api, contextHolder] = notification.useNotification();
  const {
    vehicleLimitTypes,
    vehicleLimitValueTypes,
    loading: loadingLimitType,
  } = useSelector((state) => state.addCarPlateDetail);
  const { customerVousList, vehicleDeviceTypeList, loading } = useSelector(
    (state) => state?.addCarType,
  );
  const { loading: loadingPostVehicle } = useSelector((state) => state.vehicleManagementDetail);
  const { selectedCustomer } = useSelector((state) => state?.customer);
  const { isAdmin } = useSelector((state) => state.auth);
  const { selectedUserId } = useSelector((state) => state.user);
  const [checkAll, setCheckAll] = useState(false);
  const [selectedDays, setSelectedDays] = useState([]);
  const [limitDetails, setLimitDetails] = useState({});

  const addNewDevice = useText('addNewDevice');
  const providerText = useText('provider');
  const deviceTypeText = useText('deviceType');
  const pleaseSelectLimitTypeAndLimitValueTypeFirstText = useText(
    'pleaseSelectLimitTypeAndLimitValueTypeFirst',
  );
  const transactionSuccessfullyCompleted2 = useText('transactionSuccessfullyCompleted2');
  const limitTypeText = useText('limitType');
  const limitValueTypeText = useText('limitValueType');
  const limitValueText = useText('limitValue');
  // The selected days are kept as the (localized) labels themselves, so they have to be moved
  // over to the new labels when the language is switched - otherwise the buttons look unselected
  // and the usable-days mask is rebuilt without them.
  const [monday, tuesday, wednesday, thursday, friday, saturday, sunday] = useDaysOfWeek(
    (remapDays) => {
      setSelectedDays((current) => remapDays(current));
      setLimitDetails((current) =>
        current?.selectedDays
          ? { ...current, selectedDays: remapDays(current.selectedDays) }
          : current,
      );
    },
  );

  const fetchVehicleLimitData = async () => {
    await dispatch(getCustomerVousList());
  };

  // Provider, device type and limit type labels all come from the backend and are only requested
  // once per selection, so an open modal has to ask for them again in the new language.
  useLanguageChange(async () => {
    if (!isVisible) return;
    await fetchVehicleLimitData();
    const providerTypeId = form.getFieldValue('providerTypeId');
    if (!providerTypeId) return;
    await dispatch(
      getVehicleDeviceTypeList({
        customerId: customerVousList[0]?.customerId,
        vouId: providerTypeId,
      }),
    );
    if (vehicleLimitTypes?.length) {
      await dispatch(
        getVehicleLimitTypes({
          custId: isAdmin && selectedUserId ? selectedUserId : selectedCustomer.customerId,
          vouId: providerTypeId,
        }),
      );
    }
    if (vehicleLimitValueTypes?.length) {
      await dispatch(
        getVehicleLimitValueTypes({
          custId: isAdmin && selectedUserId ? selectedUserId : selectedCustomer.customerId,
        }),
      );
    }
  });

  useEffect(() => {
    if ([customerVousList].some((data) => data.length === 0)) {
      fetchVehicleLimitData();
    }
  }, [customerVousList.length, dispatch]);

  useEffect(() => {
    try {
      if (
        vehicleListDetailDeviceTypes?.filter(
          (item) => item?.device?.includes('UTTS') && item?.providerType === 4,
        )?.length > 0 &&
        vehicleDeviceTypeList?.filter((item) => item?.vouId === 4)
      ) {
        setDontShowUTTSChoose(true);
      }
    } catch (error) {
      console.error(error);
    }
  }, [vehicleDeviceTypeList, vehicleListDetailDeviceTypes]);

  const generateFuelDaysString = (selectedDays) => {
    const daysOfWeek = [
      monday,
      tuesday,
      wednesday,
      thursday,
      friday,
      saturday,
      sunday,
    ];
    return daysOfWeek.map((day) => (selectedDays.includes(day) ? '1' : '0')).join('');
  };

  const handleCheckAllChange = (e) => {
    const checked = e.target.checked;
    const allDays = [
      monday,
      tuesday,
      wednesday,
      thursday,
      friday,
      saturday,
      sunday,
    ];
    const selectedDays = checked ? allDays : [];

    const fuelDaysString = generateFuelDaysString(selectedDays);

    setCheckAll(checked);
    setSelectedDays(selectedDays);
    setLimitDetails({
      ...limitDetails,
      checkAll: checked,
      selectedDays: selectedDays,
      fuelDays: fuelDaysString,
    });
  };

  const handleDayChange = (day) => {
    const updatedDays = selectedDays.includes(day)
      ? selectedDays.filter((d) => d !== day)
      : [...selectedDays, day];

    const fuelDaysString = generateFuelDaysString(updatedDays);

    setSelectedDays(updatedDays);
    setCheckAll(updatedDays.length === 7);
    setLimitDetails({
      ...limitDetails,
      selectedDays: updatedDays,
      fuelDays: fuelDaysString,
      checkAll: updatedDays.length === 7,
    });
  };

  const validateLimitValue = (_, value) => {
    const limitTypeId = form.getFieldValue('limitTypeId');
    const limitValueTypeId = form.getFieldValue('limitValueTypeId');

    if (!limitTypeId || !limitValueTypeId) {
      return Promise.reject(new Error(pleaseSelectLimitTypeAndLimitValueTypeFirstText));
    }

    return Promise.resolve();
  };

  const onOk = () => {
    form
      .validateFields()
      .then(async (values) => {
        const vehicles = {
          vehicleId: vehicleId,
          providerType: values.providerTypeId,
          limitType: values.limitTypeId,
          limitValueType: values.limitValueTypeId ?? 1,
          limitAmount: values.limitValue,
          usableDays: limitDetails.fuelDays ?? '1111111',
          deviceTypeId: values.deviceTypeId,
          customerId: isAdmin && selectedUserId ? selectedUserId : selectedCustomer?.customerId,
        };
        const response = await dispatch(postVehicleDeviceTypes({ vehicles })).unwrap();

        if (response.isSuccess) {
          api.success({
            message: addNewDevice,
            description: transactionSuccessfullyCompleted2,
            duration: 1,
          });
        }
        handleOk();
        form.resetFields();
        setSelectedDays([]);
        setLimitDetails({});
        setCheckAll(false);
        if (
          [customerVousList, vehicleDeviceTypeList, vehicleLimitTypes, vehicleLimitValueTypes].some(
            (data) => data.length !== 0,
          )
        ) {
          dispatch(resetCurrentLoadAddNewCarType());
          dispatch(resetVehicleLimitWithValueTypes());
        }
        setTimeout(async () => {
          await dispatch(
            getVehicleDetailList({
              customerId: isAdmin && selectedUserId ? selectedUserId : selectedCustomer.customerId,
              id: vehicleId,
            }),
          );
          await dispatch(incrementActiveTab('2'));
        }, 1500);
      })
      .catch((info) => {
        console.log('Validate Failed:', info);
      });
  };

  const onCancel = () => {
    form.resetFields(); // Formu sıfırlar
    setSelectedDays([]); // Gün seçimlerini temizler
    setCheckAll(false); // CheckAll durumunu sıfırlar
    setLimitDetails({});
    handleCancel(); // Modal'ı kapatmak için dışarıdan gelen fonksiyonu çağırır
    if (
      [customerVousList, vehicleDeviceTypeList, vehicleLimitTypes, vehicleLimitValueTypes].some(
        (data) => data.length !== 0,
      )
    ) {
      dispatch(resetCurrentLoadAddNewCarType());
      dispatch(resetVehicleLimitWithValueTypes());
    }
  };

  const handleValuesChange = async (changedValues, allValues) => {
    if (changedValues.limitTypeId) {
      return form.setFields([
        { name: 'limitValueTypeId', errors: [] },
        { name: 'limitValue', errors: [] },
      ]);
    }

    if (changedValues.providerTypeId) {
      await dispatch(
        getVehicleDeviceTypeList({
          customerId: customerVousList[0]?.customerId,
          vouId: allValues.providerTypeId,
        }),
      );
      const response = await dispatch(
        getVehicleLimitTypes({
          custId: isAdmin && selectedUserId ? selectedUserId : selectedCustomer.customerId,
          vouId: allValues.providerTypeId,
        }),
      ).unwrap();
      if (response.length > 0) {
        await dispatch(
          getVehicleLimitValueTypes({
            custId: isAdmin && selectedUserId ? selectedUserId : selectedCustomer.customerId,
          }),
        );
      }
    }
    form.setFields([
      { name: 'limitValueTypeId', errors: [] },
      { name: 'limitValue', errors: [] },
    ]);
  };

  return (
    <div className="add-vehicle-modal" data-testid="add-vehicle-model-detail-id">
      {contextHolder}
      <Modal
        title={
          <span className="custom-modal-title">
            <Text t="addNewDevice" />
          </span>
        }
        visible={isVisible}
        onOk={onOk}
        onCancel={onCancel}
        width={1200}
        className="custom-modal"
        footer={[
          <Button key="back" onClick={onCancel}>
            <Text t="giveUp" />
          </Button>,
          <Button
            key="submit"
            type="primary"
            data-testid="device-save-input"
            loading={loadingPostVehicle.postVehicleDeviceTypes}
            disabled={loadingPostVehicle.postVehicleDeviceTypes}
            onClick={onOk}
          >
            <Text t="cardParameterSave" />
          </Button>,
        ]}
      >
        <div style={{ padding: '30px 15px' }}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <div className="add-car-set-limit-type">
                <CustomForm
                  name={`add-new-vehicle-details`}
                  className="filter-form"
                  autoComplete="off"
                  initialValues={{}}
                  layout={'vertical'}
                  form={form}
                  onValuesChange={handleValuesChange}
                >
                  <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }} className="form-item">
                    <Col span={8}>
                      <CustomFormItem
                        label={<Text t="provider" />}
                        name="providerTypeId"
                        className={'delivery-address-county-form-item'}
                        rules={[{ required: true, message: <Text t="requiredField2" /> }]}
                      >
                        <CustomSpinSelect
                          id="add-car-set-providerTypeId-select"
                          data-testid="provider-input"
                          placeholder={providerText}
                          disabled={customerVousList.length === 0}
                          loading={customerVousList.length === 0}
                          height={36}
                        >
                          {customerVousList?.map((vehicle) => (
                            <OptionSpin
                              id={`add-car-set-providerTypeId-${vehicle?.vouId}-option`}
                              key={vehicle?.vouId}
                              value={vehicle?.vouId}
                            >
                              {vehicle?.vouName}
                            </OptionSpin>
                          ))}
                        </CustomSpinSelect>
                      </CustomFormItem>
                    </Col>

                    <Col span={8}>
                      <CustomFormItem
                        label={<Text t="deviceType" />}
                        data-testid="device-input"
                        name="deviceTypeId"
                        className={'delivery-address-county-form-item'}
                        rules={[{ required: true, message: <Text t="requiredField2" /> }]}
                      >
                        <CustomSpinSelect
                          id="add-car-set-deviceTypeId-select"
                          placeholder={deviceTypeText}
                          disabled={vehicleDeviceTypeList.length === 0}
                          loading={loading.vehicleDeviceTypeList}
                          height={36}
                        >
                          {dontShowUTTSChoose
                            ? vehicleDeviceTypeList
                                ?.filter((flt) => flt.deviceTypeId !== 2 && flt.deviceTypeId !== 18)
                                ?.map((vehicle) => (
                                  <OptionSpin
                                    id={`add-car-set-deviceTypeId-${vehicle?.deviceTypeId}-option`}
                                    key={vehicle?.deviceTypeId}
                                    value={vehicle?.deviceTypeId}
                                  >
                                    <Text t={vehicle?.deviceTypeLabel} />
                                  </OptionSpin>
                                ))
                            : vehicleDeviceTypeList
                                ?.filter((flt) => flt.deviceTypeId !== 2)
                                ?.map((vehicle) => (
                                  <OptionSpin
                                    id={`add-car-set-deviceTypeId-${vehicle?.deviceTypeId}-option`}
                                    key={vehicle?.deviceTypeId}
                                    value={vehicle?.deviceTypeId}
                                  >
                                    <Text t={vehicle?.deviceTypeLabel} />
                                  </OptionSpin>
                                ))}
                        </CustomSpinSelect>
                      </CustomFormItem>
                    </Col>
                  </Row>
                  <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }} className="form-item">
                    <Col span={8}>
                      <CustomFormItem
                        label={<Text t="limitType" />}
                        name="limitTypeId"
                        className={'delivery-address-county-form-item'}
                      >
                        <CustomSpinSelect
                          id="add-car-set-limit-type-limitTypeId-select"
                          placeholder={limitTypeText}
                          disabled={vehicleLimitTypes.length === 0}
                          loading={loadingLimitType.vehicleLimitTypes}
                          height={36}
                        >
                          {vehicleLimitTypes?.map((vehicle) => (
                            <OptionSpin
                              id={`add-car-set-limit-type-limitTypeId-${vehicle?.limitTypeLabel}-option`}
                              key={vehicle?.limitTypeId}
                              value={vehicle?.limitTypeId}
                            >
                              {vehicle?.limitTypeLabel}
                            </OptionSpin>
                          ))}
                        </CustomSpinSelect>
                      </CustomFormItem>
                    </Col>

                    <Col span={8}>
                      <CustomFormItem
                        label={<Text t="limitValueType" />}
                        name="limitValueTypeId"
                        dependencies={['limitTypeId']}
                        className={'delivery-address-county-form-item'}
                      >
                        <CustomSpinSelect
                          id="add-car-set-limit-type-limitValueTypeId-select"
                          placeholder={limitValueTypeText}
                          disabled={vehicleLimitTypes.length === 0}
                          loading={loadingLimitType.vehicleLimitValueTypes}
                          height={36}
                        >
                          {vehicleLimitValueTypes?.map((vehicle) => (
                            <OptionSpin
                              id={`add-car-set-limit-type-limitValueTypeId-${vehicle?.limitValueTypeLabel}-option`}
                              key={vehicle?.id}
                              value={vehicle?.id}
                            >
                              {vehicle?.limitValueTypeLabel}
                            </OptionSpin>
                          ))}
                        </CustomSpinSelect>
                      </CustomFormItem>
                    </Col>

                    <Col span={8}>
                      <CustomFormItem
                        label={<Text t="limitValue" />}
                        data-testid="limit-value-input"
                        name="limitValue"
                        dependencies={['limitTypeId', 'limitValueTypeId']}
                        className={'delivery-address-county-form-item'}
                      >
                        <CustomNumberInput
                          id="add-limit-value-numberInput"
                          autoComplete="off"
                          placeholder={limitValueText}
                          disabled={
                            vehicleDeviceTypeList.length === 0 && vehicleLimitTypes.length === 0
                          }
                          height={36}
                        />
                      </CustomFormItem>
                    </Col>
                  </Row>
                </CustomForm>
                <div className="responsive-container">
                  <div className="top-row">
                    <div className="question">
                      <Text t="whichDaysCanThisVehiclePurchaseFuel" />
                    </div>
                    <div className="checkbox-container">
                      <CustomCheckbox checked={checkAll} onChange={handleCheckAllChange}>
                        <span className="address-form-item">
                          <Text t="allDays" />
                        </span>
                      </CustomCheckbox>
                    </div>
                  </div>
                  <div className="input-container">
                    {[
                      monday,
                      tuesday,
                      wednesday,
                      thursday,
                      friday,
                      saturday,
                      sunday,
                    ].map((day) => (
                      <Button
                        data-testid="button-day-input"
                        key={day}
                        type={selectedDays.includes(day) ? 'primary' : 'default'}
                        onClick={() => handleDayChange(day)}
                      >
                        {day}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </Modal>
    </div>
  );
};

AddVehicleModal.propTypes = {
  isVisible: PropTypes.bool,
  handleOk: PropTypes.func,
  handleCancel: PropTypes.func,
  vehicleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  vehicleListDetailDeviceTypes: PropTypes.any,
};

export default AddVehicleModal;
